import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import TablePagination from '@mui/material/TablePagination';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import SyncIcon from '@mui/icons-material/Sync';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ScheduleOutlinedIcon from '@mui/icons-material/ScheduleOutlined';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import ReceiptOutlinedIcon from '@mui/icons-material/ReceiptOutlined';
import PageLayout from '../../components/layout/PageLayout';
import DataTable from '../../components/ui/DataTable';
import StatTile from '../../components/ui/StatTile';
import StatusChip from '../../components/ui/StatusChip';
import ActionIconButton from '../../components/ui/ActionIconButton';
import { notify, getErrorMessage } from '../../components/feedback/notify';
import { FormatDate } from '../../utils/FormatDate';
import { formatToBrl } from '../../utils/FormatToBrl';
import { STATUS_PAGOS, statusCobranca, metodoPagamento } from '../../utils/StatusCobranca';

// Opções do filtro de status. "Pago" agrupa os códigos do Asaas que significam pagamento recebido.
const FILTROS_STATUS = [
    { value: 'PAGO', label: 'Pago', codigos: STATUS_PAGOS },
    { value: 'PENDING', label: 'Pendente', codigos: ['PENDING'] },
    { value: 'OVERDUE', label: 'Vencido', codigos: ['OVERDUE'] },
    { value: 'REFUNDED', label: 'Estornado', codigos: ['REFUNDED'] },
    { value: 'DELETED', label: 'Removido', codigos: ['DELETED'] },
];

// O back responde sempre HTTP 200 e coloca o código real em `status` no corpo.
const MENSAGENS_ERRO = {
    400: 'Sessão inválida. Faça login novamente.',
    403: 'Seu usuário não tem permissão para ver as cobranças.',
};

const formatNum = (v) => Number(v || 0).toLocaleString('pt-BR');
const toIsoLocal = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const mesAtual = () => {
    const hoje = new Date();
    return {
        inicio: toIsoLocal(new Date(hoje.getFullYear(), hoje.getMonth(), 1)),
        fim: toIsoLocal(new Date(hoje.getFullYear(), hoje.getMonth() + 1, 0)),
    };
};

const somarResumo = (resumo, codigos) =>
    resumo
        .filter((r) => codigos.includes(r.status))
        .reduce((acc, r) => ({ total: acc.total + Number(r.total || 0), quantidade: acc.quantidade + Number(r.quantidade || 0) }), { total: 0, quantidade: 0 });

function Cobrancas() {
    const token = localStorage.getItem('token');
    const periodoInicial = useMemo(mesAtual, []);

    const [filtroStatus, setFiltroStatus] = useState([]);
    const [dataInicio, setDataInicio] = useState(periodoInicial.inicio);
    const [dataFim, setDataFim] = useState(periodoInicial.fim);
    const [cliente, setCliente] = useState(null);
    const [pagina, setPagina] = useState(0);
    const [limite, setLimite] = useState(50);

    const [cobrancas, setCobrancas] = useState([]);
    const [resumo, setResumo] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [sincronizando, setSincronizando] = useState(false);
    const [clientes, setClientes] = useState([]);
    const [recarregar, setRecarregar] = useState(0);

    useEffect(() => {
        const getClientes = async () => {
            try {
                const response = await axios.get(`${process.env.REACT_APP_API_URL}/listarUsuariosEmitirNota`, {
                    headers: { Authorization: token }
                });
                setClientes(response.data.usuarios || []);
            } catch (error) {
                console.error("Erro ao listar clientes:", error);
            }
        };
        getClientes();
    }, [token]);

    useEffect(() => {
        let ativo = true;
        const getCobrancas = async () => {
            setLoading(true);
            try {
                const status = FILTROS_STATUS
                    .filter((f) => filtroStatus.includes(f.value))
                    .flatMap((f) => f.codigos)
                    .join(',');
                const response = await axios.get(`${process.env.REACT_APP_API_URL}/cobrancasADM`, {
                    headers: { Authorization: token },
                    params: {
                        status: status || undefined,
                        dataInicio: dataInicio || undefined,
                        dataFim: dataFim || undefined,
                        idUsuario: cliente?.idUsuario,
                        pagina: pagina + 1,
                        limite,
                    }
                });
                if (!ativo) return;
                if (response.data.status !== 200) {
                    notify.error(MENSAGENS_ERRO[response.data.status] || "Não foi possível carregar as cobranças.");
                    setCobrancas([]);
                    setResumo([]);
                    setTotal(0);
                    return;
                }
                setCobrancas(response.data.cobrancas || []);
                setResumo(response.data.resumo || []);
                setTotal(Number(response.data.total || 0));
            } catch (error) {
                if (!ativo) return;
                console.error("Erro ao listar cobranças:", error);
                notify.error("Não foi possível carregar as cobranças.");
            } finally {
                if (ativo) setLoading(false);
            }
        };
        getCobrancas();
        return () => { ativo = false; };
    }, [token, filtroStatus, dataInicio, dataFim, cliente, pagina, limite, recarregar]);

    // Qualquer mudança de filtro volta para a primeira página.
    const alterarFiltro = (setter) => (valor) => {
        setter(valor);
        setPagina(0);
    };

    const handleSincronizar = async () => {
        setSincronizando(true);
        try {
            const response = await axios.post(`${process.env.REACT_APP_API_URL}/sincronizarCobrancasADM`, {}, {
                headers: { Authorization: token }
            });
            if (response.data.status !== 200) {
                notify.error(response.data.message || MENSAGENS_ERRO[response.data.status] || "Não foi possível sincronizar as cobranças.");
                return;
            }
            notify.success(response.data.message || "Cobranças sincronizadas com o Asaas.");
            setRecarregar((n) => n + 1);
        } catch (error) {
            console.error("Erro ao sincronizar cobranças:", error);
            notify.error(getErrorMessage(error, "Não foi possível sincronizar as cobranças."));
        } finally {
            setSincronizando(false);
        }
    };

    const recebido = somarResumo(resumo, STATUS_PAGOS);
    const pendente = somarResumo(resumo, ['PENDING']);
    const vencido = somarResumo(resumo, ['OVERDUE']);
    const geral = somarResumo(resumo, resumo.map((r) => r.status));

    const filtrarPorCard = (valor) => alterarFiltro(setFiltroStatus)([valor]);

    const columns = [
        { key: 'dataVencimento', label: 'Vencimento', render: (c) => (c?.dataVencimento ? FormatDate(c.dataVencimento) : '—') },
        {
            key: 'cliente',
            label: 'Cliente',
            minWidth: 220,
            render: (c) => (
                <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>{c?.usuario?.Nome || '—'}</Typography>
                    <Typography variant="caption" color="text.secondary">{c?.usuario?.Email}</Typography>
                </Box>
            ),
        },
        { key: 'descricao', label: 'Descrição', wrap: true, render: (c) => c?.descricao || '—' },
        {
            key: 'valor',
            label: 'Valor',
            align: 'right',
            render: (c) => (
                <Box>
                    <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.primary' }}>{formatToBrl(c?.valor)}</Typography>
                    {c?.valorLiquido != null && (
                        <Typography variant="caption" color="text.secondary">Líquido {formatToBrl(c.valorLiquido)}</Typography>
                    )}
                </Box>
            ),
        },
        { key: 'metodoPagamento', label: 'Forma', render: (c) => metodoPagamento(c?.metodoPagamento) },
        {
            key: 'status',
            label: 'Status',
            render: (c) => {
                const s = statusCobranca(c?.status);
                return <StatusChip label={s.label} tone={s.tone} />;
            },
        },
        { key: 'dataPagamento', label: 'Pagamento', render: (c) => (c?.dataPagamento ? FormatDate(c.dataPagamento) : '—') },
        {
            key: 'acoes',
            label: '',
            align: 'right',
            width: 64,
            render: (c) => (
                <ActionIconButton
                    title="Abrir fatura"
                    icon={<OpenInNewIcon fontSize="small" />}
                    disabled={!c?.linkFatura}
                    component="a"
                    href={c?.linkFatura}
                    target="_blank"
                    rel="noopener noreferrer"
                />
            ),
        },
    ];

    return (
        <PageLayout
            title="Cobranças"
            subtitle="Cobranças geradas no Asaas, com valores recebidos, pendentes e vencidos."
            breadcrumbs={[{ label: 'Dashboard', to: '/dashboard' }, { label: 'Cobranças' }]}
            actions={
                <Button variant="outlined" startIcon={<SyncIcon />} onClick={handleSincronizar} loading={sincronizando} loadingPosition="start">
                    Sincronizar com Asaas
                </Button>
            }
        >
            {/* Resumo respeita os filtros, mas não a paginação */}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' }, gap: 2, mb: 2 }}>
                <StatTile
                    label="Recebido"
                    icon={CheckCircleOutlineIcon}
                    loading={loading}
                    value={formatToBrl(recebido.total)}
                    helper={`${formatNum(recebido.quantidade)} cobrança(s) paga(s)`}
                    onClick={() => filtrarPorCard('PAGO')}
                />
                <StatTile
                    label="Pendente"
                    icon={ScheduleOutlinedIcon}
                    loading={loading}
                    value={formatToBrl(pendente.total)}
                    helper={`${formatNum(pendente.quantidade)} cobrança(s) a vencer`}
                    onClick={() => filtrarPorCard('PENDING')}
                />
                <StatTile
                    label="Vencido"
                    icon={ErrorOutlineIcon}
                    loading={loading}
                    value={formatToBrl(vencido.total)}
                    helper={`${formatNum(vencido.quantidade)} cobrança(s) em atraso`}
                    onClick={() => filtrarPorCard('OVERDUE')}
                />
                <StatTile
                    label="Total no período"
                    icon={ReceiptOutlinedIcon}
                    loading={loading}
                    value={formatToBrl(geral.total)}
                    helper={`${formatNum(geral.quantidade)} cobrança(s) no filtro`}
                />
            </Box>

            <DataTable
                columns={columns}
                rows={cobrancas}
                getRowKey={(c, i) => c?.idCobranca ?? i}
                loading={loading}
                emptyMessage="Nenhuma cobrança encontrada para os filtros selecionados."
                toolbar={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                        <TextField
                            select
                            size="small"
                            label="Status"
                            value={filtroStatus}
                            onChange={(event) => alterarFiltro(setFiltroStatus)(event.target.value)}
                            sx={{ width: { xs: '100%', sm: 220 } }}
                            slotProps={{
                                select: {
                                    multiple: true,
                                    renderValue: (selecionados) => selecionados.length === 0
                                        ? 'Todos'
                                        : FILTROS_STATUS.filter((f) => selecionados.includes(f.value)).map((f) => f.label).join(', '),
                                    displayEmpty: true,
                                },
                                inputLabel: { shrink: true },
                            }}
                        >
                            {FILTROS_STATUS.map((f) => (
                                <MenuItem key={f.value} value={f.value}>{f.label}</MenuItem>
                            ))}
                        </TextField>
                        <TextField
                            type="date"
                            size="small"
                            label="Vencimento de"
                            value={dataInicio}
                            onChange={(event) => alterarFiltro(setDataInicio)(event.target.value)}
                            sx={{ width: { xs: '100%', sm: 170 } }}
                            slotProps={{ inputLabel: { shrink: true } }}
                        />
                        <TextField
                            type="date"
                            size="small"
                            label="até"
                            value={dataFim}
                            onChange={(event) => alterarFiltro(setDataFim)(event.target.value)}
                            sx={{ width: { xs: '100%', sm: 170 } }}
                            slotProps={{ inputLabel: { shrink: true } }}
                        />
                        <Autocomplete
                            size="small"
                            options={clientes}
                            value={cliente}
                            onChange={(event, valor) => alterarFiltro(setCliente)(valor)}
                            getOptionLabel={(u) => u?.Nome || u?.Email || `#${u?.idUsuario}`}
                            isOptionEqualToValue={(a, b) => a?.idUsuario === b?.idUsuario}
                            renderOption={(props, u) => (
                                <li {...props} key={u.idUsuario}>
                                    <Box>
                                        <Typography variant="body2">{u?.Nome || '—'}</Typography>
                                        <Typography variant="caption" color="text.secondary">{u?.Email}</Typography>
                                    </Box>
                                </li>
                            )}
                            renderInput={(params) => <TextField {...params} label="Cliente" placeholder="Todos" slotProps={{ inputLabel: { shrink: true } }} />}
                            sx={{ width: { xs: '100%', sm: 280 } }}
                        />
                        <Button
                            size="small"
                            onClick={() => {
                                setFiltroStatus([]);
                                setDataInicio('');
                                setDataFim('');
                                setCliente(null);
                                setPagina(0);
                            }}
                            sx={{ color: 'text.secondary' }}
                        >
                            Limpar filtros
                        </Button>
                    </Box>
                }
                footer={
                    <TablePagination
                        component="div"
                        count={total}
                        page={pagina}
                        onPageChange={(event, novaPagina) => setPagina(novaPagina)}
                        rowsPerPage={limite}
                        onRowsPerPageChange={(event) => {
                            setLimite(Number(event.target.value));
                            setPagina(0);
                        }}
                        rowsPerPageOptions={[25, 50, 100, 200]}
                        labelRowsPerPage="Por página"
                        labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
                        sx={{ borderTop: '1px solid', borderColor: 'divider' }}
                    />
                }
            />
        </PageLayout>
    );
}

export default Cobrancas;
